import React, { useState, useEffect } from 'react';

const InventoryItemForm = ({ open, onClose, onSave, initialData = {}, products = [] }) => {
  const [form, setForm] = useState({
    productSkuId: '',
    sku: '',
    barcode: '',
    description: '',
    location: '',
    batch: '',
    quantity: '',
    minStock: '',
    expiryDate: '',
    itemDescription: '',
    supplierName: '',
    supplierCode: '',
    supplierContact: '',
    unitCost: '',
    storageConditions: '',
    qualityStatus: '',
    notes: ''
  });
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('sku');

  useEffect(() => {
    if (initialData && open) {
      // Handle expiry date formatting for the date input
      let formattedExpiryDate = '';
      if (initialData.expiryDate) {
        try {
          const date = new Date(initialData.expiryDate);
          if (!isNaN(date.getTime())) {
            formattedExpiryDate = date.toISOString().slice(0, 10);
          }
        } catch (e) {
          console.warn('Invalid expiry date format:', initialData.expiryDate);
        }
      }

      setForm({
        productSkuId: initialData.productSkuId || '',
        sku: initialData.productSku?.sku || '',
        barcode: initialData.productSku?.barcode || '',
        description: initialData.productSku?.description || '',
        location: initialData.location || '',
        batch: initialData.batch || '',
        quantity: initialData.quantity || '',
        minStock: initialData.minStock || '',
        expiryDate: formattedExpiryDate,
        itemDescription: initialData.itemDescription || '',
        supplierName: initialData.supplierName || '',
        supplierCode: initialData.supplierCode || '',
        supplierContact: initialData.supplierContact || '',
        unitCost: initialData.unitCost || '',
        storageConditions: initialData.storageConditions || '',
        qualityStatus: initialData.qualityStatus || '',
        notes: initialData.notes || ''
      });
    } else if (open) {
      setForm({
        productSkuId: '', sku: '', barcode: '', description: '', location: '', batch: '', quantity: '', minStock: '', expiryDate: '',
        itemDescription: '', supplierName: '', supplierCode: '', supplierContact: '',
        unitCost: '', storageConditions: '', qualityStatus: '', notes: ''
      });
    }
    setError('');
  }, [initialData, open]);

  // Flatten products and their SKUs for selection
  const flattenedProducts = products.flatMap(product =>
    product.skus ? product.skus.map(sku => ({
      ...sku,
      productName: product.name,
      productItemCode: product.itemCode,
      displayName: `${product.name} - ${sku.sku} (${sku.barcode})`
    })) : []
  );

  const filteredProducts = flattenedProducts.filter(p =>
    p.displayName.toLowerCase().includes(search.toLowerCase()) ||
    p.sku.toLowerCase().includes(search.toLowerCase()) ||
    p.barcode.toLowerCase().includes(search.toLowerCase()) ||
    p.productName.toLowerCase().includes(search.toLowerCase()) ||
    (p.productItemCode && p.productItemCode.toLowerCase().includes(search.toLowerCase()))
  );

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!form.productSkuId) return setError('Product SKU is required');
    if (!form.quantity || isNaN(form.quantity) || Number(form.quantity) <= 0) return setError('Quantity must be positive');

    // Validate SKU fields if editing
    if (initialData && initialData.id) {
      if (!form.sku || form.sku.trim() === '') return setError('SKU is required');
      if (!form.barcode || form.barcode.trim() === '') return setError('Barcode is required');
    }

    // Handle expiry date properly
    let expiryDate = null;
    if (form.expiryDate && form.expiryDate.trim() !== '') {
      const date = new Date(form.expiryDate);
      if (!isNaN(date.getTime())) {
        expiryDate = date.toISOString();
      } else {
        return setError('Invalid expiry date format');
      }
    }

    // Validate min stock
    if (form.minStock && (isNaN(form.minStock) || Number(form.minStock) < 0)) {
      return setError('Minimum stock must be a positive number');
    }

    // Validate unit cost
    if (form.unitCost && (isNaN(form.unitCost) || Number(form.unitCost) < 0)) {
      return setError('Unit cost must be a positive number');
    }

    // Prepare the data to save
    const saveData = {
      location: form.location,
      batch: form.batch,
      quantity: Number(form.quantity),
      minStock: form.minStock ? Number(form.minStock) : null,
      expiryDate: expiryDate,
      itemDescription: form.itemDescription,
      supplierName: form.supplierName,
      supplierCode: form.supplierCode,
      supplierContact: form.supplierContact,
      unitCost: form.unitCost ? Number(form.unitCost) : null,
      storageConditions: form.storageConditions,
      qualityStatus: form.qualityStatus,
      notes: form.notes
    };

    // Include SKU update data if editing
    if (initialData && initialData.id) {
      saveData.skuUpdate = {
        sku: form.sku,
        barcode: form.barcode,
        description: form.description
      };
    } else {
      // Only include productSkuId for new items
      saveData.productSkuId = form.productSkuId;
    }

    onSave(saveData);
  };

  if (!open) return null;

  const tabs = [
    { id: 'sku', label: 'SKU Selection', icon: '🏷️' },
    { id: 'basic', label: 'Inventory Info', icon: '📦' },
    { id: 'supplier', label: 'Supplier', icon: '🏢' },
    { id: 'details', label: 'Details', icon: '📋' },
    { id: 'advanced', label: 'Advanced', icon: '⚙️' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Futuristic background overlay */}
        <div className="fixed inset-0 bg-gradient-to-br from-blue-900/90 via-purple-900/90 to-indigo-900/90 backdrop-blur-sm" onClick={onClose}></div>

        {/* Modern modal panel */}
        <div className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl w-full max-w-4xl mx-auto overflow-hidden border border-white/20">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📦</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{initialData ? 'Edit' : 'Add'} Inventory Item</h2>
                  <p className="text-blue-100 text-sm">Manage your inventory with precision</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="flex space-x-1 p-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === tab.id
                    ? 'bg-white text-blue-600 shadow-sm border border-blue-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {/* SKU Selection Tab */}
            {activeTab === 'sku' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span className="text-blue-700 font-medium">SKU-Based Inventory Management</span>
                  </div>
                  <p className="text-blue-600 text-sm mt-1">
                    Select the specific SKU variant for this inventory item. All inventory operations are now based on SKU-level tracking.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Select SKU *</label>
                  {initialData && initialData.id ? (
                    <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50">
                      {(() => {
                        const selectedSku = flattenedProducts.find(p => p.id === form.productSkuId);
                        if (!selectedSku) return 'Unknown Product SKU';
                        return (
                          <div>
                            <div className="font-medium">{selectedSku.productName}</div>
                            <div className="text-xs text-gray-500">
                              SKU: {selectedSku.sku} • Barcode: {selectedSku.barcode}
                              {selectedSku.productItemCode && ` • Product Code: ${selectedSku.productItemCode}`}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Search by SKU, barcode, product name, or product code"
                        value={search || ''}
                        onChange={e => setSearch(e.target.value)}
                      />
                      <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg mt-1 bg-white">
                        {filteredProducts.map(p => (
                          <div
                            key={p.id}
                            className={`px-3 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 ${form.productSkuId === p.id ? 'bg-blue-100 text-blue-700' : ''
                              }`}
                            onClick={() => { handleChange('productSkuId', p.id); setSearch(p.displayName); }}
                          >
                            <div className="font-medium">{p.displayName}</div>
                            <div className="text-xs text-gray-500">
                              {p.productItemCode && `Product Code: ${p.productItemCode}`}
                              {p.sku && ` • SKU: ${p.sku}`}
                              {p.barcode && ` • Barcode: ${p.barcode}`}
                            </div>
                          </div>
                        ))}
                        {filteredProducts.length === 0 && <div className="px-3 py-2 text-gray-400">No SKUs found</div>}
                      </div>
                    </>
                  )}
                </div>

                {form.productSkuId && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Selected SKU Information</h4>
                    {(() => {
                      const selectedSku = flattenedProducts.find(p => p.id === form.productSkuId);
                      if (!selectedSku) return <div className="text-gray-500">No SKU selected</div>;

                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Product Name:</span>
                            <span className="ml-2 font-medium">{selectedSku.productName}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Product Code:</span>
                            <span className="ml-2 font-medium">{selectedSku.productItemCode}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">SKU Code:</span>
                            <span className="ml-2 font-medium">{selectedSku.sku}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Barcode:</span>
                            <span className="ml-2 font-medium">{selectedSku.barcode}</span>
                          </div>
                          {selectedSku.description && (
                            <div className="md:col-span-2">
                              <span className="text-gray-500">Description:</span>
                              <span className="ml-2 font-medium">{selectedSku.description}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700 font-medium">Inventory Information</span>
                  </div>
                  <p className="text-gray-600 text-sm mt-1">
                    Enter inventory-specific details for the selected SKU.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Location *</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.location || ''}
                      onChange={e => handleChange('location', e.target.value)}
                      placeholder="e.g., Warehouse A, Shelf B1"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity *</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.quantity || ''}
                      onChange={e => handleChange('quantity', e.target.value)}
                      placeholder="0"
                      min={1}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Min Stock</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.minStock || ''}
                      onChange={e => handleChange('minStock', e.target.value)}
                      placeholder="0"
                      min={0}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Unit Cost</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.unitCost || ''}
                      onChange={e => handleChange('unitCost', e.target.value)}
                      placeholder="0.00"
                      min={0}
                      step={0.01}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Batch Number</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.batch || ''}
                      onChange={e => handleChange('batch', e.target.value)}
                      placeholder="e.g., BATCH-2024-001"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date</label>
                    <input
                      type="date"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.expiryDate || ''}
                      onChange={e => handleChange('expiryDate', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Supplier Tab */}
            {activeTab === 'supplier' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Supplier Name</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.supplierName || ''}
                      onChange={e => handleChange('supplierName', e.target.value)}
                      placeholder="e.g., ABC Suppliers Ltd."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Supplier Code</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.supplierCode || ''}
                      onChange={e => handleChange('supplierCode', e.target.value)}
                      placeholder="e.g., SUP-001"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Supplier Contact</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.supplierContact || ''}
                    onChange={e => handleChange('supplierContact', e.target.value)}
                    placeholder="e.g., john@supplier.com, +1234567890"
                  />
                </div>
              </div>
            )}

            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Item Description</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.itemDescription || ''}
                    onChange={e => handleChange('itemDescription', e.target.value)}
                    placeholder="Detailed description of the item, specifications, etc."
                    rows={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Storage Conditions</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.storageConditions || ''}
                    onChange={e => handleChange('storageConditions', e.target.value)}
                    placeholder="e.g., Store in cool, dry place. Temperature: 15-25°C"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Quality Status</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.qualityStatus || ''}
                    onChange={e => handleChange('qualityStatus', e.target.value)}
                  >
                    <option value="">Select status</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending Inspection</option>
                    <option value="rejected">Rejected</option>
                    <option value="quarantine">Quarantine</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>
            )}

            {/* Advanced Tab */}
            {activeTab === 'advanced' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.notes || ''}
                    onChange={e => handleChange('notes', e.target.value)}
                    placeholder="Additional notes, special instructions, etc."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-700 font-medium">{error}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
              >
                {initialData ? 'Update Item' : 'Add Item'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InventoryItemForm; 